import type { SearchResults } from 'algoliasearch-helper';
import { useEffect, useRef } from 'react';
import { useInstantSearch } from 'react-instantsearch-core';

import { ALGOLIA_EVENT_MAX_FILTERS } from '@/integrations/algolia/const';
import { createFilterClickMiddleware } from '@/integrations/algolia/insightsOptions';
import { AlgoliaEventsNames } from '@/integrations/algolia/types';

import {
  useSendAlgoliaEvent,
  useSendViewedAlgoliaEvent,
} from './useSendAlgoliaEvent';

const FACET_REFINEMENT_TYPES = new Set(['facet', 'disjunctive']);

/**
 * Insights events of a product listing whose InstantSearch runs with
 * `insights={false}`: each list of products viewed, each set of filters
 * viewed, and the filter values clicked.
 */
export const useListingAlgoliaEvents = (
  results: SearchResults | undefined,
  objectIDs: string[],
) => {
  const { addMiddlewares } = useInstantSearch();
  const sendAlgoliaEvent = useSendAlgoliaEvent();
  const sendViewedAlgoliaEvent = useSendViewedAlgoliaEvent();
  const sentResults = useRef(new WeakSet<SearchResults>());
  const sentViews = useRef(new Set<string>());
  const sentFilterViews = useRef(new Set<string>());

  useEffect(() => {
    if (!results?.index || !objectIDs.length) return;
    // InstantSearch re-renders the previous results with new filters patched in
    // before searching, and some widgets re-run the same search when they mount.
    if (sentResults.current.has(results)) return;
    sentResults.current.add(results);

    const filters = results
      .getRefinements()
      .filter(({ type }) => FACET_REFINEMENT_TYPES.has(type))
      .map(({ attributeName, name }) => `${attributeName}:${name}`)
      .slice(0, ALGOLIA_EVENT_MAX_FILTERS);
    const viewKey = JSON.stringify([results.index, filters, objectIDs]);
    if (sentViews.current.has(viewKey)) return;
    sentViews.current.add(viewKey);

    const filterKey = JSON.stringify([results.index, filters]);
    const isNewFilterView = !sentFilterViews.current.has(filterKey);
    sentFilterViews.current.add(filterKey);

    sendViewedAlgoliaEvent(
      objectIDs,
      results.index,
      isNewFilterView ? filters : [],
    );
  }, [results]);

  useEffect(
    () =>
      addMiddlewares(
        createFilterClickMiddleware((index, clickedFilters) =>
          sendAlgoliaEvent({
            event: AlgoliaEventsNames.clickedFilters,
            indexName: index,
            filters: clickedFilters,
          }),
        ),
      ),
    [addMiddlewares, sendAlgoliaEvent],
  );
};
